import React from "react";
import { Box, Button, ButtonBase, Link, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAuth } from "../../../common/auth";
import { C } from "../../../common/theme";
import { TrafficSessionView, TrafficViewDetail, getTrafficView } from "../../../common/requests/platform/endpoints";
import { CopyValue, DetailDialog, DetailList, DetailSection, ErrorState, StatusPill } from "../../../ui";
import { formatDate } from "../../Sales/salesData";
import { deviceLabel, errorMessage, formatCount, formatMs } from "../platformData";

const SITE = "https://inflos.co";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
/** Click ids that name where a visit came from even without a referrer */
const CLICK_IDS: Record<string, string> = {
  fbclid: "Facebook / Instagram link",
  gclid: "Google Ads",
  srsltid: "Google Shopping / Search",
  ttclid: "TikTok",
};

const pad = (n: number) => String(n).padStart(2, "0");

/** "8 Oct 2026 18:43:34" in the browser's time zone */
function formatFull(iso: string | null | undefined) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** "+1:05" (m:ss) or "+1:02:05" since the visit's first view */
function formatOffset(fromIso: string | null | undefined, toIso: string | null | undefined) {
  if (!fromIso || !toIso) return "";
  const s = Math.max(0, Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `+${h}:${pad(m)}:${pad(s % 60)}` : `+${m}:${pad(s % 60)}`;
}

function queryOf(path: string): URLSearchParams {
  const i = path.indexOf("?");
  return new URLSearchParams(i >= 0 ? path.slice(i + 1).split("#")[0] : "");
}

/** In-app navigation stores the previous path; arrivals store the full URL. */
function referrerText(referrer: string | null | undefined): { label: string; external: boolean } {
  if (!referrer) return { label: "None (typed, bookmarked, or from an app)", external: true };
  if (referrer.startsWith("/")) return { label: referrer, external: false };
  try {
    const url = new URL(referrer);
    const ours = url.hostname === "inflos.co" || url.hostname.endsWith(".inflos.co");
    return { label: ours ? `${url.pathname}${url.search}` : url.hostname, external: !ours };
  } catch {
    return { label: referrer, external: true };
  }
}

/** Where the visit came from, from its first view: referrer, campaign tags, click ids. */
function originOf(entry: TrafficSessionView | undefined) {
  if (!entry) return null;
  const query = queryOf(entry.path);
  const utm = UTM_KEYS.filter((k) => query.get(k)).map((k) => [k.replace("utm_", ""), query.get(k) as string] as const);
  const clicks = Object.keys(CLICK_IDS)
    .filter((k) => query.has(k))
    .map((k) => CLICK_IDS[k]);
  const ref = referrerText(entry.referrer);
  let summary: string;
  if (utm.length) summary = [query.get("utm_source"), query.get("utm_medium")].filter(Boolean).join(" / ");
  else if (clicks.length) summary = clicks[0];
  else if (entry.referrer && ref.external) summary = ref.label;
  else if (!entry.referrer) summary = "Direct";
  else summary = "Already on inflos.co";
  return { summary, utm, clicks, referrer: ref, search: query.get("q") };
}

type Item = { label: string; value: React.ReactNode; full?: boolean };

/** Drops rows with nothing to show, like the old dl did. */
const present = (items: (Item | null | false | undefined)[]): Item[] =>
  items.filter((i): i is Item => !!i && i.value !== null && i.value !== undefined && i.value !== "");

interface Props {
  /** The view to show; null closes the dialog */
  eventId: string | null;
  onClose: () => void;
}

/** Everything stored about one page view, and the visit it belongs to. */
export default function ViewDetailDialog({ eventId, onClose }: Props) {
  const { idToken } = useAuth();
  // Starts at the clicked row; picking another page of the visit moves it.
  const [current, setCurrent] = React.useState<string | null>(eventId);
  const [data, setData] = React.useState<TrafficViewDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    setCurrent(eventId);
  }, [eventId]);

  React.useEffect(() => {
    if (!current || !idToken) return;
    const ac = new AbortController();
    setError(null);
    getTrafficView(idToken, current, ac.signal)
      .then((res) => !ac.signal.aborted && setData(res.data))
      .catch((e) => !ac.signal.aborted && setError(errorMessage(e, "Couldn't load this page view")));
    return () => ac.abort();
  }, [current, idToken, attempt]);

  const shown = data && data.view.event_id === current ? data : null;
  const v = shown?.view;
  const visit = shown?.session.views ?? [];
  const entry = visit[0];
  const origin = originOf(entry);
  const thisRef = v ? referrerText(v.referrer) : null;

  return (
    <DetailDialog
      open={!!eventId}
      onClose={onClose}
      size="md"
      title={v ? <Box component="span" className="mono" sx={{ fontSize: "0.85em" }}>{v.path}</Box> : "Page view"}
      subtitle={v ? `Page view · ${formatFull(v.created_at)}` : undefined}
      status={v?.is_internal ? <StatusPill tone="outline">Internal</StatusPill> : undefined}
      loading={!error && (!v || !shown)}
      actions={
        <>
          {v && (
            <Button variant="outlined" href={`${SITE}${v.path}`} target="_blank" rel="noreferrer" endIcon={<OpenInNewIcon fontSize="small" />}>
              Open page
            </Button>
          )}
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : v && shown ? (
        <>
          <DetailSection title="Where it came from">
            <DetailList
              items={present([
                { label: "Source", value: <Box component="span" sx={{ fontWeight: 700 }}>{origin?.summary ?? "Unknown"}</Box> },
                entry && {
                  label: "Landing page",
                  value: (
                    <>
                      <Box component="span" className="mono" sx={{ fontSize: 13 }}>
                        {entry.path}
                      </Box>{" "}
                      <Typography component="span" variant="caption" sx={{ color: C.muted }}>
                        at {formatFull(entry.created_at)}
                      </Typography>
                    </>
                  ),
                  full: true,
                },
                { label: "Referrer", value: origin ? origin.referrer.label : null },
                ...(origin?.utm.map(([k, value]) => ({ label: `UTM ${k}`, value })) ?? []),
                origin?.clicks.length ? { label: "Click id", value: origin.clicks.join(", ") } : null,
                thisRef && entry && entry.event_id !== v.event_id
                  ? {
                      label: "This page from",
                      value: (
                        <Box component="span" className={thisRef.external ? undefined : "mono"}>
                          {thisRef.label}
                        </Box>
                      ),
                    }
                  : null,
              ])}
            />
          </DetailSection>

          <DetailSection title="Who">
            <DetailList
              items={present([
                {
                  label: "User",
                  value: shown.user ? (
                    <>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {shown.user.username ?? `user ${shown.user.user_id.slice(0, 8)}`}
                      </Box>
                      {shown.user.email ? ` · ${shown.user.email}` : ""}
                    </>
                  ) : (
                    "Not signed in"
                  ),
                },
                {
                  label: "This browser",
                  value: shown.visitor
                    ? `${formatCount(shown.visitor.views)} views over ${formatCount(shown.visitor.sessions)} visits · first seen ${formatFull(shown.visitor.first_seen)}`
                    : "Not tracked (before 8 Oct 2026)",
                },
                v.is_internal ? { label: "Internal", value: <StatusPill tone="outline">Our own browser</StatusPill> } : null,
              ])}
            />
          </DetailSection>

          <DetailSection title="Location">
            <DetailList
              items={present([
                { label: "Country", value: v.country || "Unknown" },
                { label: "Region", value: v.region },
                {
                  label: "IP address",
                  value: v.ip_address ? (
                    <Link href={`https://ipinfo.io/${v.ip_address}`} target="_blank" rel="noreferrer" className="mono" sx={{ color: C.ink }}>
                      {v.ip_address}
                    </Link>
                  ) : null,
                },
                { label: "Language", value: v.language },
              ])}
            />
          </DetailSection>

          <DetailSection title="Device">
            <DetailList
              items={present([
                { label: "Device", value: deviceLabel(v.device_type) },
                { label: "OS", value: [v.os_name, v.os_version].filter(Boolean).join(" ") || null },
                { label: "Browser", value: [v.browser_name, v.browser_version].filter(Boolean).join(" ") || null },
                { label: "Screen", value: v.screen_width && v.screen_height ? `${v.screen_width} × ${v.screen_height}` : null },
                {
                  label: "Page load",
                  value: v.page_load_time_ms || v.ttfb_ms ? `${formatMs(v.page_load_time_ms)} · first byte ${formatMs(v.ttfb_ms)}` : null,
                },
                {
                  label: "User agent",
                  full: true,
                  value: v.user_agent ? (
                    <Box component="span" className="mono" sx={{ fontSize: 12, color: C.ink2, fontWeight: 400 }}>
                      {v.user_agent}
                    </Box>
                  ) : null,
                },
              ])}
            />
          </DetailSection>

          <DetailSection title={`This visit · ${visit.length}${shown.session.truncated ? "+" : ""} page${visit.length === 1 ? "" : "s"}`}>
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", border: `1px solid ${C.line}`, borderRadius: "8px", overflow: "hidden" }}>
              {visit.map((step, i) => {
                const isCurrent = step.event_id === v.event_id;
                const clickable = !isCurrent && !!step.event_id;
                return (
                  <Box component="li" key={step.event_id ?? step.created_at} sx={{ borderTop: i ? `1px solid ${C.line}` : 0 }}>
                    <ButtonBase
                      disabled={!clickable}
                      onClick={() => clickable && setCurrent(step.event_id as string)}
                      aria-current={isCurrent ? "page" : undefined}
                      sx={{
                        width: "100%",
                        minHeight: 44,
                        display: "grid",
                        gridTemplateColumns: "60px minmax(0, 1fr) auto",
                        columnGap: 1,
                        alignItems: "center",
                        textAlign: "left",
                        px: 1.25,
                        py: 0.5,
                        bgcolor: isCurrent ? C.bone1 : undefined,
                        "&:hover": clickable ? { bgcolor: C.head } : undefined,
                        "&.Mui-disabled": { color: "inherit" },
                        "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: -2 },
                      }}
                    >
                      <Typography variant="caption" className="num" sx={{ color: C.muted, whiteSpace: "nowrap" }}>
                        {formatOffset(entry?.created_at, step.created_at)}
                      </Typography>
                      <Typography variant="body2" noWrap title={step.path} sx={{ fontWeight: isCurrent ? 700 : 400 }}>
                        {step.path}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.muted, whiteSpace: "nowrap" }}>
                        {step.signed_in ? "signed in" : ""}
                      </Typography>
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>
          </DetailSection>

          <DetailSection title="IDs">
            <DetailList
              columns={3}
              items={present([
                { label: "View", value: v.event_id ? <CopyValue value={v.event_id} mono /> : null },
                { label: "Session", value: shown.session.session_id ? <CopyValue value={shown.session.session_id} mono /> : null },
                { label: "Visitor", value: v.visitor_id ? <CopyValue value={v.visitor_id} mono /> : null },
              ])}
            />
          </DetailSection>
        </>
      ) : null}
    </DetailDialog>
  );
}
